import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  Renderer2,
  forwardRef,
  OnDestroy,
  inject
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';

import { Subscription } from 'rxjs';
import { ApiConstants } from 'src/app/base/api-constants';
import { DMConstants } from 'src/app/pages/designs/data-model/data-model.component';
import { UtilService } from 'src/app/services/util.service';
import { AppLoaderService } from 'src/app/services/app-loader.service';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { ReferenceUtilService } from '../reference-tree-view/reference-util.service';
import { ActionType, ModelType, UiActions } from '../reference-tree-view/reference.enum';
@AutoUnsubscribe({ arrayName: 'subscriptions' })
@Component({
  selector: 'vb-tbl-type-dropdown',
  templateUrl: './tbl-type-dropdown.component.html',
  styleUrls: ['./tbl-type-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TblTypeDropdownComponent),
      multi: true
    }
  ],
  standalone: false  //NOSONAR
})
export class TblTypeDropdownComponent implements OnDestroy {
  private confirmationDialogService = inject(ConfirmationDialogService);
  private renderer = inject(Renderer2);
  private elementRef = inject(ElementRef);
  private util = inject(UtilService);
  private appGlobalService = inject(AppGlobalService);
  private loader = inject(AppLoaderService);
  private cd = inject(ChangeDetectorRef);
  private referenceUtil = inject(ReferenceUtilService);

  @Input() element: any;
  @Input() formData: any;
  @Input() options: any;
  @Input() disableField = false;
  @Input() showRdbmsProps = false;
  backupValue: string;
  queryView = 'Query View';
  value: string;
  private modelValue: string;
  excluded = ['TABLE_INDEX', 'TABLE_FOREIGN_KEY', 'TABLE_LOOKUP_DEFINITION'];
  private onChange: (m: any) => void;
  private onTouched: () => void;
  subscriptions: Subscription[] = [];

  get model() {
    return this.modelValue;
  }

  writeValue(value: any): void {
    this.value = value;
    if (value !== 'query_view' && this.options && this.options?.length > 0) {
      this.options = this.options.filter((opt) => opt.value !== 'query_view');
    }
    this.backupValue = value;
    this.modelValue = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  set(value: any) {
    if (this.backupValue !== this.value) {
      this.backupValue = value;
      this.modelValue = value;
      this.onChange(this.modelValue);
    }
  }

  setDisabledState(isDisabled: boolean) {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
    // this.disabled = isDisabled;
  }

  onTblTypChange(event) {
    let typeHasImpact = false;
    if (this.formData?.name === 'Application User') {
      if (!(this.value === DMConstants.TRANS_ANALY || this.value === DMConstants.TRANS_ONLY)) {
        this.util.showMessage('error', 'Application User should be Transaction only or Transaction + Analytical');
        setTimeout(() => {
          this.value = this.backupValue;
        }, 100);
        return;
      }
    }
    if (
      (this.value === DMConstants.ANALY_ONLY || this.value === DMConstants.COMPLEX_TYPE) &&
      (this.formData.t_type === DMConstants.TRANS_ONLY || this.formData.t_type === DMConstants.TRANS_ANALY)
    ) {
      typeHasImpact = true;
    }
    this.checkReferences(event, typeHasImpact);
  }

  private resolveActionType(previousType: string, currentType: string): ActionType {
    switch (true) {
      case currentType === DMConstants.TRANSIENT:
        return ActionType.TABLE_TYPE_UPDATE_TO_TRANSIENT;
      case currentType === DMConstants.COMPLEX_TYPE:
        return ActionType.TABLE_TYPE_UPDATE_TO_COMPOSITE;
      case currentType === DMConstants.ANALY_ONLY:
        return ActionType.TABLE_TYPE_UPDATE_TO_ANALYTICAL;
      case previousType === DMConstants.COMPLEX_TYPE && currentType !== DMConstants.COMPLEX_TYPE:
        return ActionType.TABLE_TYPE_UPDATE_FROM_COMPOSITE;
      default:
        return ActionType.TABLE_TYPE_UPDATE;
    }
  }

  checkReferences(event: any, typeHasImpact: boolean) {
    this.loader.show();
    const node = { name: this.formData?.name || this.formData?.workflowName, type: 'Table' };
    const previousType = this.backupValue;
    const currentType = this.value;
    const actionType = this.resolveActionType(previousType, currentType);
    this.referenceUtil
      .handleWithReferences({
        payload: {
          modelId: this.formData.sid,
          modelType: ModelType.TABLE,
          actionType: actionType,
          action: UiActions.UPDATE
        },
        node,
        extras: { typeHasImpact },
        onConfirm: () => this.updateTableType(),
        onNoReferences: () => this.updateTableType(),
        onCancel: () => {
          this.value = this.backupValue;
        },
        onError: () => {
          this.value = this.backupValue;
        }
      })
      .catch(() => {
        this.value = this.backupValue;
      })
      .finally(() => this.loader.hide());
  }

  updateTableType() {
    const impProp = this.formData.t_importProps;
    const expProp = this.formData.t_exportProps;
    if (
      (!this.showRdbmsProps &&
        (this.backupValue === DMConstants.ANALY_ONLY || this.backupValue === DMConstants.TRANS_ANALY) &&
        (this.value === DMConstants.TRANS_ONLY ||
          this.value === DMConstants.COMPLEX_TYPE ||
          this.value === DMConstants.TRANSIENT) &&
        expProp) ||
      ((this.backupValue === DMConstants.TRANS_ONLY || this.backupValue === DMConstants.TRANS_ANALY) &&
        (this.value === DMConstants.ANALY_ONLY ||
          this.value === DMConstants.COMPLEX_TYPE ||
          this.value === DMConstants.TRANSIENT) &&
        impProp)
    ) {
      let text;
      if (impProp && expProp) {
        text = `${this.value !== DMConstants.TRANS_ONLY ? 'imports and' : ''} exports`;
      } else if (impProp && this.value !== DMConstants.TRANS_ONLY) {
        text = 'imports';
      } else {
        text = 'exports';
      }
      this.confirmationDialogService.confirm({
        message: `Changing the table type will clear the ${text}. Are you sure want to change the type?`,
        accept: () => {
          this.set(this.value);
        },
        reject: () => {
          this.value = this.backupValue;
          return;
        }
      });
    } else {
      this.set(this.value);
    }
  }

  addToSubscriptionsList(subName) {
    this.subscriptions.push(subName);
  }

  ngOnDestroy(): void {
    //AutoUnsubscribe
    $(this.elementRef.nativeElement).remove();
  }
}



